import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlusIcon from '@wix/da-ds/pkg/Icons/Plus';
import ElementQueries from '@wix/da-shared-react/pkg/ElementQueries';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { URLS } from '@wix/da-url';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { ModuleType } from '../../../../types/modules';
import WidgetTypeCard from './WidgetTypeCard';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ZoneType, InstallableModuleType } from '../../../../types/layout';
import { moduleTypeToReferralType } from './helpers';

import s from './AddWidget.scss';

export type InstallableModule = {
  moduleName: ModuleType;
  zone: ZoneType;
  position?: number;
};

export type InstallableModuleInfo = InstallableModule & InstallableModuleType;

export interface Props extends WithSectionContextProps {
  installableModules: InstallableModule[];
  installableModulesInfo: InstallableModuleInfo[];
  hasCoreMembership?: boolean;
  disabled?: boolean;
  addModule: (zone: ZoneType, moduleType: ModuleType, position: number) => void;
}

const AddWidget: React.FC<Props> = ({
  section,
  installableModulesInfo,
  hasCoreMembership,
  disabled,
  addModule,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const logBiEvent = useBiLogger();

  const rootRef = useRef<HTMLDivElement>(null);
  useOnOutsideClick([rootRef], () => setIsMenuOpen(false), true);

  return (
    <ErrorBoundary debugComponent="AddWidget" hideOnFail>
      <div
        className={classnames(
          s['root'],
          disabled && s['disabled'],
          isMenuOpen && s['menu-is-open']
        )}
        data-role="add-widget"
        ref={rootRef}
      >
        <div className={s['line']}>
          <PlainButtonOrLink
            biData={
              isMenuOpen
                ? undefined
                : {
                    evid: BiEventType.ADD_WIDGET_CLICK,
                  }
            }
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={s['button-with-gradient-line']}
          >
            <div className={s['menu-toggle-btn']}>
              <PlusIcon className={s['plus-icon']} size={IconSize.SMALL} />
              {t(!isMenuOpen ? 'widgets.common.addWidget' : 'common.close')}
            </div>
          </PlainButtonOrLink>

          {isMenuOpen && (
            <ElementQueries
              widthBreakpoints={[920]}
              className={s['menu-container']}
            >
              <div className={s['menu']} data-role="menu">
                {installableModulesInfo.map(
                  (
                    installableModuleInfo: InstallableModuleInfo,
                    idx: number
                  ) => {
                    const {
                      zone,
                      moduleName = null,
                      position = 0,
                      limit,
                      restrict,
                      installedCount = 0,
                    } = installableModuleInfo;

                    const needsCoreForThis =
                      restrict &&
                      restrict.priv === 'core' &&
                      !hasCoreMembership &&
                      installedCount >= limit;

                    return (
                      <div
                        className={s['menu-item']}
                        key={idx}
                        onClick={() => {
                          if (needsCoreForThis) {
                            const referral_info =
                              moduleTypeToReferralType(moduleName);
                            logBiEvent({
                              evid: BiEventType.BUY_CORE_BUTTON,
                              referral_info,
                            });
                            window &&
                              window.open(URLS.CORE_MEMBERSHIP, '_blank');
                          }

                          if (zone && moduleName) {
                            addModule && addModule(zone, moduleName, position);

                            logBiEvent({
                              evid: BiEventType.ADD_WIDGET,
                              widgetname: moduleName,
                              widgetzone: zone,
                            });
                          }

                          setIsMenuOpen(false);
                        }}
                      >
                        <WidgetTypeCard
                          section={section}
                          zone={installableModuleInfo.zone}
                          moduleName={installableModuleInfo.moduleName}
                          hasCoreMembership={hasCoreMembership}
                        />
                      </div>
                    );
                  }
                )}
              </div>
            </ElementQueries>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

AddWidget.displayName = 'AddWidget';

export default AddWidget;
